import React from 'react';
import { FLOW, useNavigation } from 'hooks/useNavigation';
import { RouteKeys } from 'models/RouteModels';
import { Patient } from 'models/PatientModels';
import MDButton from 'components/atoms/MDButton/MDButton';
import { Breakpoints, MUIColors } from 'models/StyleModels';
import PDF from 'components/molecules/PDF/PDF';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faCircleNotch, faDownload, faPrint } from '@fortawesome/free-solid-svg-icons';
import colors from 'assets/theme/base/colors';
import { useBreakpoints } from 'hooks/useBreakpoints';
import MDTypography from 'components/atoms/MDTypography/MDTypography';
import { Appointment } from 'models/AppointmentModels';

import {
    ReportContainer,
    PdfPlaceholderContainer,
    SmallLoaderBoxesContainer,
    MediumLoaderBoxesContainer,
    ButtonGridContainer,
    PdfViewerContainer,
    ActionButtonsContainer,
    CompleteEvaluationButtonContainer,
    LoaderBoxLg,
    LoaderBoxSm,
    LoaderBoxMd,
    PdfPlaceholder
} from './TriageExamReportContent.styles';

interface TriageExamReportContentProps {
    pdfFile?: Blob | null;
    pdfLoading?: boolean;
    pdfError?: boolean;
    appointment: Appointment | null;
    activePatient: Patient | null;
    queriesLoading: boolean;
    isVisible?: boolean;
    generatePdf?: () => void;
}

const TriageExamReportContent: React.FC<TriageExamReportContentProps> = ({
    pdfFile: externalPdfFile,
    pdfLoading: externalPdfLoading,
    pdfError: externalPdfError,
    appointment,
    activePatient,
    queriesLoading: initQueriesLoading,
    generatePdf: generatePdf,
    isVisible = true
}) => {

    const { navigateToBaseRouteWithAptmtDetails } = useNavigation();

    const { breakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.MEDIUM });
    const { breakpointBreached: lgBreakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.LARGE });

    const pdfLoading = externalPdfLoading !== undefined ? externalPdfLoading : false;
    const pdfError = externalPdfError !== undefined ? externalPdfError : false;
    const pdfFile = externalPdfFile !== undefined ? externalPdfFile : null;

    // opens up the PDF in the browser's native PDF viewer
    const handlePrintPdf = (blob: Blob | null) => {
        if (!blob) return;
        const url = window.URL.createObjectURL(blob);
        const newWindow = window.open(url, '_blank');
        if (newWindow) newWindow.focus();
        window.URL.revokeObjectURL(url);
    };

    // creates a dummy download link on the DOM and clicks it
    // to initiate the PDF download
    const handleDownloadPdf = (
        blob: Blob | null,
        patient: Patient | null,
    ) => {
        if (!blob || !patient) return;
        const downloadLink = document.createElement('a');
        const url = URL.createObjectURL(blob);
        downloadLink.href = url;
        downloadLink.download = `patient_report-${patient?.last_name}-${new Date().toISOString()}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
    };

    const generatePdfPlaceholder = (mode: 'loading' | 'error') => (
        <PdfPlaceholder $breakpointBreached={breakpointBreached}>
            <PdfPlaceholderContainer>
                <MDTypography color={mode === 'loading' ? MUIColors.PRIMARY : MUIColors.ERROR} textAlign='center' mb={2} variant='h3'>
                    {
                        mode === 'loading'
                            ? <>Please wait while <br /> we generate your report.</>
                            : <>There was a problem <br /> generating your report.</>
                    }
                </MDTypography>
                {
                    mode === 'loading'
                        ? <FontAwesomeIcon icon={faCircleNotch} color={colors.primary.main} spin size="2x" />
                        : (
                            <MDButton
                                variant='contained'
                                color={MUIColors.ERROR}
                                onClick={() => generatePdf?.()}
                            >
                                Retry
                            </MDButton>
                        )
                }
            </PdfPlaceholderContainer>
            <LoaderBoxLg className='TriageExamReportPage__LoaderBoxLg' />
            <SmallLoaderBoxesContainer>
                {Array.from(new Array(3), (el, index) => (
                    <LoaderBoxSm
                        key={`box_${index + 1}`}
                        className='TriageExamReportPage__LoaderBoxSm'
                    />
                ))}
            </SmallLoaderBoxesContainer>
            <MediumLoaderBoxesContainer>
                {Array.from(new Array(2), (el, index) => (
                    <LoaderBoxMd
                        key={`box_${index + 1}`}
                        className='TriageExamReportPage__LoaderBoxMd'
                    />
                ))}
            </MediumLoaderBoxesContainer>
        </PdfPlaceholder>
    );

    // Don't render if not visible (for performance)
    if (!isVisible) {
        return null;
    }

    return (
        <>
            {lgBreakpointBreached ?
                <>
                    <MDButton
                        fullWidth={lgBreakpointBreached}
                        variant='outlined'
                        startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
                        color={MUIColors.SECONDARY}
                        onClick={() => navigateToBaseRouteWithAptmtDetails({
                            routeKey: RouteKeys.EXAM_ROOM_ALL_JOINTS,
                            appointmentId: appointment!.appointment_id,
                            appointmentDateISOString: appointment!.appointment_date,
                            addtlQueryParams: { flow: FLOW.TRIAGE }
                        })}
                        style={{ marginTop: '1rem' }}
                    >
                        Exam Room
                    </MDButton>
                    <MDButton
                        variant='contained'
                        fullWidth={lgBreakpointBreached}
                        style={{ height: 'fit-content', marginTop: '1rem' }}
                        color={MUIColors.SECONDARY}
                        onClick={() => navigateToBaseRouteWithAptmtDetails({
                            routeKey: RouteKeys.TRIAGE_LIST,
                            appointmentId: appointment!.appointment_id,
                            appointmentDateISOString: appointment!.appointment_date,
                            addtlQueryParams: { flow: FLOW.TRIAGE }
                        })}
                    >
                        Complete Evaluation
                    </MDButton>
                    <ButtonGridContainer>
                        <MDButton
                            startIcon={<FontAwesomeIcon icon={faPrint} />}
                            disabled={pdfLoading || !pdfFile}
                            style={{ color: colors.secondary.main, gridColumnStart: '2' }}
                            onClick={() => handleDownloadPdf(pdfFile, activePatient)}
                        >
                            Download Report
                        </MDButton>
                        <MDButton
                            startIcon={<FontAwesomeIcon icon={faDownload} />}
                            disabled={pdfLoading || !pdfFile}
                            style={{ color: colors.secondary.main, gridColumnStart: '3' }}
                            onClick={() => handlePrintPdf(pdfFile)}
                        >
                            Print Report
                        </MDButton>
                    </ButtonGridContainer>
                </> : ''}
            <ReportContainer $breakpointBreached={breakpointBreached}>
                {lgBreakpointBreached ? '' :
                    <div>
                        <MDButton
                            variant='outlined'
                            startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
                            color={MUIColors.SECONDARY}
                            onClick={() => navigateToBaseRouteWithAptmtDetails({
                                routeKey: RouteKeys.EXAM_ROOM_ALL_JOINTS,
                                appointmentId: appointment!.appointment_id,
                                appointmentDateISOString: appointment!.appointment_date,
                                addtlQueryParams: { flow: FLOW.TRIAGE }
                            })}
                        >
                            Exam Room
                        </MDButton>
                    </div>}
                <PdfViewerContainer $lgBreakpointBreached={lgBreakpointBreached}>
                    {!pdfFile && (initQueriesLoading || pdfLoading || pdfError) && generatePdfPlaceholder(pdfError ? 'error' : 'loading')}

                    {pdfFile && <PDF src={pdfFile} fadeOutBottom />}
                </PdfViewerContainer>
                {lgBreakpointBreached ? '' :
                    <ActionButtonsContainer $breakpointBreached={breakpointBreached}>
                        <div>
                            <MDButton
                                startIcon={<FontAwesomeIcon icon={faPrint} />}
                                disabled={pdfLoading || !pdfFile}
                                style={{ color: colors.secondary.main }}
                                onClick={() => handleDownloadPdf(pdfFile, activePatient)}
                            >
                                Download Report
                            </MDButton>
                            <MDButton
                                startIcon={<FontAwesomeIcon icon={faDownload} />}
                                disabled={pdfLoading || !pdfFile}
                                style={{ color: colors.secondary.main }}
                                onClick={() => handlePrintPdf(pdfFile)}
                            >
                                Print Report
                            </MDButton>
                        </div>
                        {lgBreakpointBreached ? '' :
                            <CompleteEvaluationButtonContainer>
                                <MDButton
                                    variant='contained'
                                    style={{ height: 'fit-content' }}
                                    color={MUIColors.SECONDARY}
                                    onClick={() => navigateToBaseRouteWithAptmtDetails({
                                        routeKey: RouteKeys.TRIAGE_LIST,
                                        appointmentId: appointment!.appointment_id,
                                        appointmentDateISOString: appointment!.appointment_date,
                                        addtlQueryParams: { flow: FLOW.TRIAGE }
                                    })}
                                >
                                    Complete Evaluation
                                </MDButton>
                            </CompleteEvaluationButtonContainer>}
                    </ActionButtonsContainer>}
            </ReportContainer>
        </>);
};

export default TriageExamReportContent;

